import { type NoyauCode, noyauCodeSchema } from '@rosetta/shared';
import { z } from 'zod';
import { ecrireJson, lireJson } from './stockage';

export const CLE_BROUILLON = 'rosetta.brouillon';

/** What the input screen keeps while the user works: not yet a valid request. */
export const brouillonSchema = z.object({
  titre: z.string(),
  texte: z.string(),
  noyaux: z.array(noyauCodeSchema),
  /**
   * Set by "relancer" (#5): the identifier of the failed translation this draft relaunches,
   * so that its card leaves the history once the new translation is sent.
   */
  relanceDe: z.string().min(1).optional(),
});
export type Brouillon = z.infer<typeof brouillonSchema>;

export function lireBrouillon(): Brouillon | null {
  return lireJson(window.sessionStorage, CLE_BROUILLON, brouillonSchema);
}

/** The storage refused to keep a value: it is full (`QuotaExceededError`, by any of its names). */
function stockagePlein(erreur: unknown): boolean {
  return (
    erreur instanceof DOMException &&
    (erreur.name === 'QuotaExceededError' || erreur.name === 'NS_ERROR_DOM_QUOTA_REACHED')
  );
}

/**
 * Keeps the draft for the tab. Returns `false` when the storage is full (B2, #13: a text of
 * several megabytes pasted in the zone): the draft is then not kept, and the one stored before is
 * removed, so that a reload never brings back an older text than the one on screen. The screen
 * goes on with the text in memory; any other error of the storage is not this one and is thrown.
 */
export function enregistrerBrouillon(brouillon: Brouillon): boolean {
  try {
    ecrireJson(window.sessionStorage, CLE_BROUILLON, brouillon);
    return true;
  } catch (erreur) {
    if (!stockagePlein(erreur)) throw erreur;
    window.sessionStorage.removeItem(CLE_BROUILLON);
    return false;
  }
}

/**
 * Empties the draft kept for the tab. Called by "nouveau texte", by the launch of a translation
 * (#24: once it is sent, its text must not come back on the input screen) and by the logout.
 */
export function viderBrouillon(): void {
  window.sessionStorage.removeItem(CLE_BROUILLON);
}

/**
 * What "relancer" hands to the input screen (#24; "modifier le texte" was removed with #37). It is carried in memory,
 * for one opening of the screen, and never written in the draft of the tab: the screen takes it
 * when it opens (or when it is told, if it is already open), shows it and keeps it out of
 * `sessionStorage`. Leaving that screen without translating therefore leaves nothing behind, and
 * the next input screen is empty.
 */
let reprise: Brouillon | null = null;

/** The text waiting for the input screen, if "relancer" left one. */
export function lireReprise(): Brouillon | null {
  return reprise;
}

/** The input screen has taken the text it was handed (or left): nothing waits any more. */
export function viderReprise(): void {
  reprise = null;
}

type EcouteurRelance = () => void;
const ecouteursRelance = new Set<EcouteurRelance>();

/**
 * Hands `brouillon` to the input screen for its next opening, and tells the screen that is
 * already open (a toast "relancer" shows on any page, the input one included).
 */
export function preparerReprise(brouillon: Brouillon): void {
  reprise = brouillon;
  for (const ecouteur of ecouteursRelance) ecouteur();
}

/** What "relancer" needs from a failed translation, whether it comes from a card or a toast. */
export interface EchecARelancer {
  id: string;
  titre?: string | undefined;
  texteOrigine: string;
  noyaux: readonly NoyauCode[];
}

/**
 * "Relancer": the input screen opens with the title, the text and the noyaux of the failed
 * translation (the one in progress is replaced, without asking). Nothing
 * is sent: the user still has to click "traduire". From a partial translation (#13) the noyaux
 * are the ones that did not succeed: the caller passes them.
 */
export function preparerRelance(echec: EchecARelancer): void {
  preparerReprise({
    titre: echec.titre ?? '',
    texte: echec.texteOrigine,
    noyaux: [...echec.noyaux],
    relanceDe: echec.id,
  });
}

/**
 * Calls `ecouteur` after each `preparerReprise`; returns the way to stop. The input
 * screen listens, because a "relancer" clicked while it is already open does not mount
 * it again: it must take the text handed to it.
 */
export function ecouterRelance(ecouteur: EcouteurRelance): () => void {
  ecouteursRelance.add(ecouteur);
  return () => {
    ecouteursRelance.delete(ecouteur);
  };
}

type EcouteurVidage = () => void;
const ecouteursVidage = new Set<EcouteurVidage>();

/**
 * "Accueil" (the home button of the navbar and the logo of the header, #26): the input screen of the next arrival is an
 * empty one, and the one that is already open empties itself on screen. Empties the draft of the
 * tab and what waits in memory (as "nouveau texte" does), then tells the open input screen.
 */
export function viderSaisie(): void {
  viderBrouillon();
  viderReprise();
  for (const ecouteur of ecouteursVidage) ecouteur();
}

/**
 * `onClick` of the two links to the home page, the home button of the navbar and the logo of the
 * header (#26): a plain click (or Enter) empties the input. A click with Ctrl, Meta, Shift or Alt
 * opens the link elsewhere (a new tab, a window): the browser's own behaviour is kept and nothing
 * is emptied here. The middle click does not reach `onClick`.
 */
export function viderSaisieAuClic(evenement: {
  button: number;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}): void {
  if (evenement.button !== 0) return;
  if (evenement.ctrlKey || evenement.metaKey || evenement.shiftKey || evenement.altKey) return;
  viderSaisie();
}

/** Calls `ecouteur` after each `viderSaisie`; returns the way to stop. */
export function ecouterVidage(ecouteur: EcouteurVidage): () => void {
  ecouteursVidage.add(ecouteur);
  return () => {
    ecouteursVidage.delete(ecouteur);
  };
}
